import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

@Component({
  selector: 'app-date-picker',
  imports: [NgClass],
  templateUrl: './date-picker.html',
})
export class DatePickerComponent implements OnInit, OnDestroy {
  value = input<string>(''); // YYYY-MM-DDTHH:MM  or ''
  placeholder = input('Select date…');
  disabled = input<boolean>(false);
  error = input<boolean>(false);
  hideTime = input<boolean>(false);
  valueChange = output<string>();

  protected open = signal(false);
  protected dropdownTop = signal(0);
  protected dropdownLeft = signal(0);
  protected dropdownMinWidth = signal(0);
  protected openUpward = signal(false);
  protected showTimeError = signal(false);
  private blurTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly DROPDOWN_HEIGHT = 460;
  protected viewMonth = signal(new Date().getMonth());
  protected viewYear = signal(new Date().getFullYear());
  // Time is picked in 12h (JCCA-124) but the value is still emitted in 24h (YYYY-MM-DDTHH:MM).
  protected hour = signal(''); // '1'..'12' or ''
  protected minute = signal(''); // '00'..'59' or ''
  protected period = signal<'AM' | 'PM'>('AM');
  protected timeSet = signal(false);
  // Hour/minute use in-panel lists instead of native <select>: Chrome on Windows draws a
  // 60-option native list as an OS popup that can land outside the window.
  protected openTimeList = signal<'hour' | 'minute' | null>(null);

  protected readonly DAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  protected readonly HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
  protected readonly MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  private computePosition(): void {
    const rect = (this.el.nativeElement as HTMLElement).getBoundingClientRect();
    const h = DatePickerComponent.DROPDOWN_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom;
    const up = spaceBelow < h && rect.top > spaceBelow;
    const rawTop = up ? rect.top - h - 6 : rect.bottom + 6;
    this.openUpward.set(up);
    this.dropdownLeft.set(rect.left);
    this.dropdownMinWidth.set(rect.width);
    this.dropdownTop.set(Math.max(8, Math.min(rawTop, window.innerHeight - h - 8)));
  }

  private reposition = (): void => {
    if (!this.open()) return;
    this.computePosition();
  };

  ngOnInit(): void {
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
    // Capture phase: modals stop click propagation, so a bubbling document listener never fires.
    document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
    if (this.blurTimer !== null) clearTimeout(this.blurTimer);
  }

  constructor(private el: ElementRef) {
    effect(() => {
      const v = this.value();
      const [datePart, timePart] = v ? v.split('T') : ['', ''];
      if (timePart) {
        const h = timePart.slice(0, 2);
        const min = timePart.slice(3, 5);
        // 00:00 is also what date-only picks emit, so it's read as "no time yet".
        if (h !== '00' || min !== '00') {
          const t = to12h(parseInt(h, 10));
          this.hour.set(t.hour);
          this.period.set(t.period);
          this.minute.set(min);
          this.timeSet.set(true);
        }
      }
      if (datePart) {
        const d = new Date(datePart + 'T00:00:00');
        if (!isNaN(d.getTime())) {
          this.viewMonth.set(d.getMonth());
          this.viewYear.set(d.getFullYear());
        }
      }
    });
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  protected label = computed(() => {
    const v = this.value();
    if (!v) return '';
    const [datePart, timePart] = v.split('T');
    if (!datePart) return '';
    const d = new Date(datePart + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(2);
    if (this.hideTime()) return `${mm}/${dd}/${yy}`;
    const hasNonZeroTime =
      timePart && (timePart.slice(0, 2) !== '00' || timePart.slice(3, 5) !== '00');
    if (!this.timeSet() && !hasNonZeroTime) return `${mm}/${dd}/${yy}`;
    const [h, min] = (timePart || '00:00').split(':');
    const t = to12h(parseInt(h, 10));
    return `${mm}/${dd}/${yy} ${t.hour}:${min} ${t.period}`;
  });

  protected selectedDate = computed(() => {
    const [datePart] = this.value().split('T');
    if (!datePart) return null;
    const d = new Date(datePart + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  });

  protected monthLabel = computed(() => `${MONTHS[this.viewMonth()]} ${this.viewYear()}`);
  protected weeks = computed(() => buildWeeks(this.viewYear(), this.viewMonth()));

  // ── Navigation ─────────────────────────────────────────────────────────────

  protected prev(): void {
    const { m, y } = behind(this.viewMonth(), this.viewYear());
    this.viewMonth.set(m);
    this.viewYear.set(y);
  }

  protected next(): void {
    const { m, y } = ahead(this.viewMonth(), this.viewYear());
    this.viewMonth.set(m);
    this.viewYear.set(y);
  }

  // ── Interactions ───────────────────────────────────────────────────────────

  protected toggle(): void {
    if (this.disabled()) return;
    if (!this.open()) this.computePosition();
    this.open.update((v) => !v);
  }

  protected selectDay(day: Date): void {
    const datePart = toDatePart(day);
    if (this.hideTime()) {
      this.hour.set('00');
      this.minute.set('00');
      this.timeSet.set(true);
      this.valueChange.emit(`${datePart}T00:00`);
      this.open.set(false);
      return;
    }
    if (this.timeSet()) {
      this.emit(datePart);
    } else {
      this.valueChange.emit(datePart);
    }
  }

  // ── Time (JCCA-115 / JCCA-124): hour and minute selects + AM/PM ─────────────

  protected onHourChange(value: string): void {
    this.hour.set(value);
    this.openTimeList.set(null);
    this.afterTimeChange();
  }

  protected onMinuteChange(value: string): void {
    this.minute.set(value);
    this.openTimeList.set(null);
    this.afterTimeChange();
  }

  protected toggleTimeList(list: 'hour' | 'minute'): void {
    const next = this.openTimeList() === list ? null : list;
    this.openTimeList.set(next);
    if (next) {
      // Bring the current value into view once the list renders.
      setTimeout(() =>
        (this.el.nativeElement as HTMLElement)
          .querySelector('[data-time-option-selected]')
          ?.scrollIntoView({ block: 'center' }),
      );
    }
  }

  /** Clicks anywhere in the panel outside a time list close it. */
  protected onPanelPointerDown(e: PointerEvent): void {
    if (!this.openTimeList()) return;
    if (!(e.target as HTMLElement).closest('[data-time-list]')) this.openTimeList.set(null);
  }

  protected setPeriod(period: 'AM' | 'PM'): void {
    if (period === this.period()) return;
    this.period.set(period);
    this.afterTimeChange();
  }

  /** Current time, and today's date if no day is picked yet. */
  protected setNow(): void {
    const now = new Date();
    const t = to12h(now.getHours());
    this.hour.set(t.hour);
    this.period.set(t.period);
    this.minute.set(String(now.getMinutes()).padStart(2, '0'));
    this.timeSet.set(true);
    this.showTimeError.set(false);
    const [datePart] = this.value().split('T');
    this.emit(datePart || toDatePart(now));
    if (!datePart) {
      this.viewMonth.set(now.getMonth());
      this.viewYear.set(now.getFullYear());
    }
  }

  private afterTimeChange(): void {
    if (!this.hour() || !this.minute()) return;
    this.timeSet.set(true);
    this.showTimeError.set(false);
    this.emitWithCurrentDate();
  }

  protected clear(): void {
    this.openTimeList.set(null);
    this.valueChange.emit('');
    this.timeSet.set(false);
    this.hour.set('');
    this.minute.set('');
    this.period.set('AM');
    this.showTimeError.set(false);
    this.open.set(false);
  }

  protected tryClose(): void {
    if (this.hideTime() || !this.selectedDate() || this.timeSet()) {
      this.showTimeError.set(false);
      this.open.set(false);
    } else {
      this.showTimeError.set(true);
    }
  }

  protected isSelected(day: Date): boolean {
    const sel = this.selectedDate();
    return !!sel && sameDay(day, sel);
  }

  protected isToday(day: Date): boolean {
    return sameDay(day, new Date());
  }

  // ── Emit ───────────────────────────────────────────────────────────────────

  private emit(datePart: string): void {
    const hh = String(to24h(parseInt(this.hour(), 10), this.period())).padStart(2, '0');
    this.valueChange.emit(`${datePart}T${hh}:${this.minute()}`);
  }

  private emitWithCurrentDate(): void {
    const [datePart] = this.value().split('T');
    if (datePart) this.emit(datePart);
  }

  // ── Close on outside click ─────────────────────────────────────────────────

  private onOutsidePointerDown = (e: PointerEvent): void => {
    if (this.open() && !this.el.nativeElement.contains(e.target as Node)) this.tryClose();
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function toDatePart(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** 0–23 → 12h hour ('1'..'12') + period. 0 → 12 AM, 12 → 12 PM. */
function to12h(h24: number): { hour: string; period: 'AM' | 'PM' } {
  const h = isNaN(h24) ? 0 : h24;
  return { hour: String(h % 12 || 12), period: h >= 12 ? 'PM' : 'AM' };
}

/** 12h hour + period → 0–23. 12 AM → 0, 12 PM → 12. */
function to24h(h12: number, period: 'AM' | 'PM'): number {
  const h = h12 % 12;
  return period === 'PM' ? h + 12 : h;
}

function buildWeeks(y: number, m: number): (Date | null)[][] {
  const offset = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells: (Date | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7) cells.push(null);
  const rows: (Date | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}

function ahead(m: number, y: number) {
  return m === 11 ? { m: 0, y: y + 1 } : { m: m + 1, y };
}
function behind(m: number, y: number) {
  return m === 0 ? { m: 11, y: y - 1 } : { m: m - 1, y };
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
